import React, { useState } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { IVendas } from './interfaces/IVendas';
import { FaShoppingCart, FaUser, FaStore, FaCreditCard } from 'react-icons/fa';
import Format from '../../../Services/Format';
import LayoutTable from '../../data/LayoutTable';
import { Btn } from '../../data/Btn';
import ActiveInactiv from '../../data/activeInactive';
import * as S from './styledView';

/* ─── helpers ─── */
const fmt = (v: number) =>
  v?.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) ?? '---';

const fmtDate = (d: string | undefined, pattern: string) => {
  if (!d) return '---';
  return Format.date(new Date(d), pattern);
};

const NA = 'Não informado';

/* ─── component ─── */
interface Props {
  venda: IVendas | undefined;
  onBack: () => void;
  loading?: boolean;
  backLabel?: string;
  hideTopBar?: boolean;
}

export default function SaleDetailView({ venda, onBack, loading, backLabel = 'Voltar', hideTopBar }: Props) {
  const [key, setKey] = useState('produtos');

  if (loading || !venda || !venda.relationships) {
    return (
      <>
        {!hideTopBar && (
          <S.TopBar>
            <Btn text={backLabel} variante="secondary" onClick={onBack} />
          </S.TopBar>
        )}
        <LayoutTable>
          <S.LoadingBox>Carregando dados da venda...</S.LoadingBox>
        </LayoutTable>
      </>
    );
  }

  const r = venda.relationships;

  // A API retorna cada item duplicado: versão do PDV (self_service: false, sem descrição)
  // e versão do totem (self_service: true, com item_description preenchido).
  // Exibimos apenas os itens com self_service: true pois têm nome e representam o pedido real.
  // Se não houver nenhum assim, exibe todos como fallback.
  const products = (() => {
    const rawProducts = Array.isArray(r?.products) ? r.products : [];
    const selfServiceItems = rawProducts.filter((p: any) => p.self_service === true);
    return selfServiceItems.length > 0 ? selfServiceItems : rawProducts;
  })();

  return (
    <>
      {!hideTopBar && (
        <S.TopBar>
          <Btn text={backLabel} variante="secondary" onClick={onBack} />
        </S.TopBar>
      )}

      <LayoutTable>
        <S.HeaderRow>
          <S.TitleGroup>
            <h3>Venda #{venda.order_number}</h3>
            <ActiveInactiv
              status={!venda.canceled}
              text={venda.canceled ? 'Cancelada' : 'Ativa'}
              color={venda.canceled ? '#e4684b' : undefined}
            />
            {venda.self_service && (
              <ActiveInactiv status text="Autoatendimento" color="#0f57cd" />
            )}
          </S.TitleGroup>
          <S.NetValue>{fmt(venda.net_value)}</S.NetValue>
        </S.HeaderRow>

        <S.MetaGrid>
          <S.MetaItem>
            <S.MetaLabel>Data</S.MetaLabel>
            <S.MetaValue>{fmtDate(venda.created_at, 'DD/MM/YYYY')}</S.MetaValue>
          </S.MetaItem>
          <S.MetaItem>
            <S.MetaLabel>Hora</S.MetaLabel>
            <S.MetaValue>{fmtDate(venda.created_at, 'HH:mm:ss')}</S.MetaValue>
          </S.MetaItem>
          <S.MetaItem>
            <S.MetaLabel>Turno</S.MetaLabel>
            <S.MetaValue>Turno {venda.shift}</S.MetaValue>
          </S.MetaItem>
          <S.MetaItem>
            <S.MetaLabel>Documento</S.MetaLabel>
            <S.MetaValue>{venda.document || NA}</S.MetaValue>
          </S.MetaItem>
          <S.MetaItem>
            <S.MetaLabel>Operação</S.MetaLabel>
            <S.MetaValue style={{ textTransform: 'capitalize' }}>{venda.operation}</S.MetaValue>
          </S.MetaItem>
          <S.MetaItem>
            <S.MetaLabel>Forma de Pagamento</S.MetaLabel>
            <S.MetaValue>{r.paymentType?.description ?? NA}</S.MetaValue>
          </S.MetaItem>
        </S.MetaGrid>
      </LayoutTable>

      <LayoutTable>
        <Tabs activeKey={key} onSelect={(k: any) => setKey(k)} className="mt-2">
          <Tab eventKey="produtos" title={`Produtos (${products.length})`}>
            <S.TabContent>
              {products.length === 0 ? (
                <S.EmptyState>Nenhum produto registrado nesta venda.</S.EmptyState>
              ) : (
                <S.ProductTable>
                  <thead>
                    <tr>
                      <th>Qtd.</th>
                      <th>Descrição</th>
                      <th>Terminal</th>
                      <th>Unitário</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={p.id}>
                        <td>{p.sale_qty}</td>
                        <td>
                          {p.item_description || `Produto #${p.product_id}`}
                          {p.self_service && (
                            <S.SelfServiceTag>Self-service</S.SelfServiceTag>
                          )}
                        </td>
                        <td>{p.terminal}</td>
                        <td>{fmt(p.unit_sale_value)}</td>
                        <td>{fmt(p.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </S.ProductTable>
              )}
            </S.TabContent>
          </Tab>

          <Tab eventKey="info" title="Informações">
            <S.TabContent>
              <S.SectionTitle><FaStore size={13} /> Loja &amp; Venda</S.SectionTitle>
              <S.InfoGrid>
                <S.InfoCell><S.InfoLabel>Loja ID</S.InfoLabel><S.InfoValue>{venda.store_id}</S.InfoValue></S.InfoCell>
                <S.InfoCell><S.InfoLabel>Terminal</S.InfoLabel><S.InfoValue>{venda.terminal}</S.InfoValue></S.InfoCell>
                <S.InfoCell><S.InfoLabel>ID PDV</S.InfoLabel><S.InfoValue>{venda.id_pdv}</S.InfoValue></S.InfoCell>
                <S.InfoCell><S.InfoLabel>Nº Pedido</S.InfoLabel><S.InfoValue>#{venda.order_number}</S.InfoValue></S.InfoCell>
                <S.InfoCell><S.InfoLabel>Série</S.InfoLabel><S.InfoValue>{venda.serie_id || '---'}</S.InfoValue></S.InfoCell>
                <S.InfoCell><S.InfoLabel>Número</S.InfoLabel><S.InfoValue>{venda.number}</S.InfoValue></S.InfoCell>
                <S.InfoCell><S.InfoLabel>Qtd. Pessoas</S.InfoLabel><S.InfoValue>{venda.people_qty}</S.InfoValue></S.InfoCell>
                <S.InfoCell><S.InfoLabel>Origem</S.InfoLabel><S.InfoValue>{r.origin?.name ?? NA}</S.InfoValue></S.InfoCell>
                <S.InfoCell><S.InfoLabel>Status</S.InfoLabel><S.InfoValue>{r.situation?.name ?? NA}</S.InfoValue></S.InfoCell>
                <S.InfoCell><S.InfoLabel>Autoatendimento</S.InfoLabel><S.InfoValue>{venda.self_service ? 'Sim' : 'Não'}</S.InfoValue></S.InfoCell>
              </S.InfoGrid>

              <S.SectionTitle><FaUser size={13} /> Consumidor</S.SectionTitle>
              <S.InfoGrid>
                <S.InfoCell><S.InfoLabel>CPF/CNPJ</S.InfoLabel><S.InfoValue>{r.customer?.cpf_cnpj ?? NA}</S.InfoValue></S.InfoCell>
                <S.InfoCell><S.InfoLabel>Nome</S.InfoLabel><S.InfoValue>{r.customer?.name ?? NA}</S.InfoValue></S.InfoCell>
              </S.InfoGrid>

              <S.SectionTitle><FaCreditCard size={13} /> Pagamento</S.SectionTitle>
              <S.InfoGrid>
                <S.InfoCell><S.InfoLabel>Troco</S.InfoLabel><S.InfoValue>{fmt(venda.change)}</S.InfoValue></S.InfoCell>
                <S.InfoCell><S.InfoLabel>Gorjeta</S.InfoLabel><S.InfoValue>{fmt(venda.tip_value)}</S.InfoValue></S.InfoCell>
              </S.InfoGrid>
            </S.TabContent>
          </Tab>

          <Tab eventKey="operador" title="Operador">
            <S.TabContent>
              {r.operator ? (
                <>
                  <S.SectionTitle><FaUser size={13} /> Dados do Operador</S.SectionTitle>
                  <S.InfoGrid>
                    <S.InfoCell><S.InfoLabel>Nome</S.InfoLabel><S.InfoValue>{r.operator.name}</S.InfoValue></S.InfoCell>
                    <S.InfoCell><S.InfoLabel>E-mail</S.InfoLabel><S.InfoValue>{r.operator.email}</S.InfoValue></S.InfoCell>
                    <S.InfoCell><S.InfoLabel>CPF/CNPJ</S.InfoLabel><S.InfoValue>{r.operator.cpf_cnpj}</S.InfoValue></S.InfoCell>
                    <S.InfoCell><S.InfoLabel>Status</S.InfoLabel><S.InfoValue>{r.operator.status === 1 ? 'Ativo' : 'Inativo'}</S.InfoValue></S.InfoCell>
                    <S.InfoCell><S.InfoLabel>Funcionário</S.InfoLabel><S.InfoValue>{r.operator.is_employee ? 'Sim' : 'Não'}</S.InfoValue></S.InfoCell>
                    <S.InfoCell><S.InfoLabel>Nascimento</S.InfoLabel><S.InfoValue>{fmtDate(r.operator.birthdate, 'DD/MM/YYYY')}</S.InfoValue></S.InfoCell>
                  </S.InfoGrid>

                  <S.SectionTitle><FaUser size={13} /> Garçom / Vendedor</S.SectionTitle>
                  <S.InfoGrid>
                    <S.InfoCell><S.InfoLabel>Garçom</S.InfoLabel><S.InfoValue>{r.waiter?.name ?? NA}</S.InfoValue></S.InfoCell>
                    <S.InfoCell><S.InfoLabel>Vendedor</S.InfoLabel><S.InfoValue>{r.vendor?.name ?? NA}</S.InfoValue></S.InfoCell>
                  </S.InfoGrid>
                </>
              ) : (
                <S.EmptyState>Informações do operador não disponíveis.</S.EmptyState>
              )}
            </S.TabContent>
          </Tab>
        </Tabs>
      </LayoutTable>

      <LayoutTable>
        <S.SectionTitle><FaShoppingCart size={13} /> Resumo</S.SectionTitle>
        <S.SummaryGrid>
          <S.SummaryLine>
            <span>Valor Bruto</span>
            <span>{fmt(venda.gross_value)}</span>
          </S.SummaryLine>
          <S.SummaryLine>
            <span>Serviço</span>
            <span>{fmt(venda.service_value)}</span>
          </S.SummaryLine>
          <S.SummaryLine>
            <span>Descontos</span>
            <span style={{ color: 'var(--error)' }}>- {fmt(venda.discount)}</span>
          </S.SummaryLine>
          <S.TotalLine>
            <span>Total Líquido</span>
            <span>{fmt(venda.net_value)}</span>
          </S.TotalLine>
        </S.SummaryGrid>
      </LayoutTable>
    </>
  );
}
